/**
 * Oturum baglami.
 *
 * ── SIDEBAR SUNUCUDAN GELIR ──────────────────────────────────────────────────
 * `me.nav` dizisini sunucu uretir; istemci "hangi menu gorunsun" hesabi YAPMAZ.
 * Iki istemci (web + masaustu) ayni menuyu cizsin diye. Bu bir guvenlik onlemi
 * DEGIL — koruma her uc noktada ayrica var. Amac tutarlilik.
 *
 * ── ACILIS SIRASI ────────────────────────────────────────────────────────────
 *   1. `restoreSession()` — httpOnly cerez varsa access token'i geri alir
 *   2. `/me` — kullanici, yetkiler, kredi, menu
 *   3. Ikisi de basarisizsa: giris ekrani
 * Bu bitene kadar UYGULAMA CIZILMEZ; aksi halde kullanici bir an giris
 * ekranini gorup sonra ana sayfaya atlar — hatali ve rahatsiz edici.
 *
 * ── OTURUM DEGISINCE ONBELLEK SILINIR ────────────────────────────────────────
 * React Query onbellegi kullaniciya gore anahtarlanmiyor: `['catalog','owned-
 * categories']` iki hesapta da ayni anahtar. Onbellek temizlenmezse yonetici
 * cikip musteri girdiginde, `staleTime` suresince ONCEKI hesabin cevabi
 * gosterilir — musterinin satin aldigi katalog sidebar'da gorunmez, ancak
 * sayfa yenilenince gelir. Bu yuzden oturumun bittigi HER yolda `clear()`:
 * cikis, 401 ile dusme ve yeni giris.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setSessionEndedHandler } from './api';
import type { LanguagePreference, MeResponse, NavKey, ThemePreference } from '@shared/index';

interface LoginResult {
  accessToken?: string;
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
}

export type LoginOutcome =
  | { requiresTwoFactor: true; twoFactorToken: string }
  | { requiresTwoFactor: false; me: MeResponse };

interface AuthContextValue {
  me: MeResponse | null;
  loading: boolean;
  /** Basari donusu MeResponse: cagiran, nav'a gore dogru sayfaya yonlensin. */
  login: (email: string, password: string, remember: boolean, twoFactorToken?: string, code?: string) => Promise<LoginOutcome>;
  /** Google onay kapısından dönen erişim jetonunu güvenli oturuma alır. */
  completeExternalSession: (accessToken: string) => Promise<MeResponse>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  /** Yetki kontrolu — arayuz kolayligi, koruma degil. */
  can: (nav: NavKey) => boolean;
  hasProduct: (productId: string) => boolean;
  setThemePreference: (theme: ThemePreference) => void;
  setLanguagePreference: (language: LanguagePreference) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    const data = await api.get<MeResponse>('/me');
    setMe(data);
    return data;
  }, []);

  useEffect(() => {
    setSessionEndedHandler(() => {
      setAccessToken(null);
      setMe(null);
      queryClient.clear();
    });

    let cancelled = false;

    void (async () => {
      try {
        const restored = await api.restoreSession();
        if (restored && !cancelled) await loadMe();
      } catch {
        // Sessiz: oturum yok demek. Giris ekrani zaten dogru yer.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMe, queryClient]);

  const login = useCallback(
    async (email: string, password: string, remember: boolean, twoFactorToken?: string, code?: string): Promise<LoginOutcome> => {
      const result = await api.post<LoginResult>(twoFactorToken ? '/auth/2fa/verify' : '/auth/login', twoFactorToken ? {
        twoFactorToken,
        code,
        rememberMe: remember,
        client: 'web',
      } : { email, password, rememberMe: remember, client: 'web' });
      if (result.requiresTwoFactor && result.twoFactorToken) {
        return { requiresTwoFactor: true, twoFactorToken: result.twoFactorToken };
      }
      if (!result.accessToken) throw new Error('Oturum yanıtı eksik.');
      setAccessToken(result.accessToken);
      // Onceki hesabin sorgulari kalmasin: giren kullanici temiz baslar.
      queryClient.clear();
      return { requiresTwoFactor: false, me: await loadMe() };
    },
    [loadMe, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', { client: 'web' });
    } finally {
      // Sunucu hata verse bile YEREL oturum kapanir. Kullanicinin "cikis yaptim"
      // beklentisi, sunucunun yanit vermesine bagli olmamali.
      setAccessToken(null);
      setMe(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const completeExternalSession = useCallback(
    async (token: string) => {
      setAccessToken(token);
      queryClient.clear();
      return await loadMe();
    },
    [loadMe, queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      loading,
      login,
      completeExternalSession,
      logout,
      reload: async () => {
        await loadMe();
      },
      can: (nav) => Boolean(me?.nav.includes(nav)),
      hasProduct: (productId) => Boolean(me?.entitlements.some((e) => e.productId === productId)),
      setThemePreference: (theme) => {
        setMe((prev) => (prev ? { ...prev, user: { ...prev.user, theme } } : prev));
      },
      setLanguagePreference: (language) => {
        setMe((prev) => (prev ? { ...prev, user: { ...prev.user, language } } : prev));
      },
    }),
    [me, loading, login, completeExternalSession, logout, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  return ctx;
}

/** Oturum SART olan yerlerde: kullanici kesin var. */
export function useMe(): MeResponse {
  const { me } = useAuth();
  if (!me) throw new Error('useMe korumalı bir rota dışında çağrıldı.');
  return me;
}
